/**
 * STRIP WHAT CAN RUN, KEEP WHAT WAS AUTHORED.
 *
 * Every surface that renders a teacher's stored prose does it with `dangerouslySetInnerHTML`,
 * because the content IS markup — chips carrying `data-answer-ref-alias`, KaTeX's spans and
 * MathML, an inline SVG structure, ink colours set as inline styles. None of that can survive a
 * round trip through `textContent`, so the markup has to be rendered.
 *
 * What it must NOT carry is anything that executes. Measured before this existed: a block whose
 * stored html held `<img onerror>`, `<a href="javascript:">`, `<svg onload>` and an `<iframe>`
 * rendered all four intact the moment `isPreview` was true. The editor refuses to CREATE them --
 * TipTap's schema has no such nodes -- but the editor is not the only writer: `BlockDto.data` is
 * an untyped object, so anything holding a teacher token can store them, and experiences are
 * built to be shared and adapted between teachers. Importing a colleague's experience would have
 * run their script in your students' browsers.
 *
 * AN ATTRIBUTE STRIPPER, NOT AN ALLOWLIST, and that is the deliberate choice. An allowlist of
 * elements would have to enumerate everything KaTeX, OpenChemLib and the chip renderers can
 * emit, and would silently eat the next thing they learn to emit. What makes content dangerous
 * is a short, closed list -- handlers, script-bearing URLs, and the few elements that fetch or
 * execute by existing -- so that is what this removes. Images, spans, SVG, MathML, styles and
 * every `data-` attribute pass through untouched, because all of them are load-bearing here.
 *
 * Parsed with `DOMParser`, which is INERT: it runs no script and fetches no resource. Assigning
 * to a detached `div.innerHTML` is not -- an `<img src>` in a detached element still hits the
 * network in a browser, which is a tracking beacon even when the handler never fires.
 *
 * This is a backstop, not a substitute for validating what gets stored. See the note in the
 * overnight report about capping and shaping `BlockDto.data` on the way in.
 */

/** Elements that run, fetch, or redirect simply by existing. */
const BANNED = new Set([
  'SCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'BASE', 'META', 'LINK', 'FORM', 'NOSCRIPT',
]);

/** Attributes whose value is a URL, so whose scheme has to be checked. */
const URL_ATTRS = ['href', 'src', 'xlink:href', 'action', 'formaction', 'poster', 'data'];

/** Schemes that execute or smuggle a document. `data:` images are fine and stay. */
const BAD_SCHEME = /^\s*(javascript|vbscript|data:text\/html|data:application)/i;

export function sanitizeAuthoredHtml(html: string): string {
  if (!html || !html.includes('<')) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const el of Array.from(doc.body.querySelectorAll('*'))) {
    if (BANNED.has(el.tagName)) { el.remove(); continue; }
    for (const attr of Array.from(el.attributes)) {
      /* Already lower case: the HTML parser normalises attribute names, including inside
         foreign content. Lowered anyway as insurance, and deliberately NOT claimed by a test --
         a mutation removing it passes, because nothing can reach it. */
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) { el.removeAttribute(attr.name); continue; }
      /*
       * CONTROL CHARACTERS ARE THE POINT, which is why the rule is off for this line rather
       * than the expression rewritten. `javascript&#9;:alert(1)` and its NUL-padded cousins are
       * exactly how a scheme check is evaded, so they are stripped BEFORE the scheme is read.
       * A regex that refused to mention them could not do that.
       */
      // eslint-disable-next-line no-control-regex
      if (URL_ATTRS.includes(name) && BAD_SCHEME.test(attr.value.replace(/[\u0000- ]/g, ''))) {
        el.removeAttribute(attr.name);
      }
    }
  }
  return doc.body.innerHTML;
}
